import { useState } from "react";
import { X } from "lucide-react";
import SEO from "../components/SEO";

const galleryImages = [
  { src: "/images/ongoing/06-galaxy-blue-sapphire.jpg", title: "Galaxy Blue Sapphire Plaza", category: "ongoing" },
  { src: "/images/ongoing/07-iit-bhubaneswar.jpg", title: "Indian Institute Of Technology", category: "ongoing" },
  { src: "/images/ongoing/08-imt-raj-nagar.jpg", title: "Institute of Management Technology", category: "ongoing" },
  { src: "/images/ongoing/09-jayabheri-trendset.jpg", title: "Trendset Jayabheri Elevate", category: "ongoing" },
  { src: "/images/ongoing/10-police-commissionerate.jpg", title: "Police Commissionerate", category: "ongoing" },
  { src: "/images/ongoing/01-ajnara-panorama-noida.jpg", title: "Ajnara Panorama", category: "ongoing" },
  { src: "/images/ongoing/02-ambuja-cement.jpg", title: "Ambuja Cement Plant", category: "ongoing" },
  { src: "/images/ongoing/04-dial-phase.jpg", title: "DIAL Phase 3A Expansion", category: "ongoing" },
  { src: "/images/ongoing/05-embassy-oxygen.jpg", title: "Embassy Oxygen Business Park", category: "ongoing" },
  { src: "/images/complete/01-aiims-kalyani.jpg", title: "All India Institute of Medical Sciences (AIIMS)", category: "completed" },
  { src: "/images/complete/02-air-india.jpg", title: "Air India Hangar", category: "completed" },
  { src: "/images/complete/03-my-home-bhooja.jpg", title: "My Home Bhooja", category: "completed" },
  { src: "/images/complete/04-my-home-villas.jpg", title: "My Home Villas", category: "completed" },
  { src: "/images/complete/05-orient-paper-mill.jpg", title: "Orient Paper Mill", category: "completed" },
  { src: "/images/complete/06-tata-chemicals.jpg", title: "Tata Chemicals Limited", category: "completed" },
  { src: "/images/complete/07-vegas-mall-dwarka.jpg", title: "Vegas Mall", category: "completed" },
  { src: "/images/new/11.jpg", title: "Facility View", category: "facility" },
  { src: "/images/new/12.jpg", title: "Facility View", category: "facility" },
  { src: "/images/new/13.jpg", title: "Facility View", category: "facility" },
  { src: "/images/new/14.jpg", title: "Facility View", category: "facility" },
  { src: "/images/assets/safety-standards.JPG", title: "Safety Standards", category: "facility" },
  { src: "/images/assets/high-rise-project.jpeg", title: "High-Rise Project", category: "facility" },
  { src: "/images/assets/quality-assurance.jpeg", title: "Quality Assurance", category: "facility" },
  { src: "/images/assets/site-execution.jpeg", title: "Site Execution", category: "facility" },
];

export default function GalleryPage() {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  return (
    <div data-testid="gallery-page">
      <SEO title="Gallery" description="View our portfolio of scaffolding projects across India. High-rise buildings, industrial plants, infrastructure projects. See our work quality firsthand." canonical="/gallery" />
      {/* Hero */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" data-testid="gallery-headline">Gallery</h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            Explore our portfolio of successful projects and facilities
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {galleryImages.map((img, index) => (
              <div
                key={index}
                className="group relative aspect-square rounded-lg overflow-hidden cursor-pointer shadow-md hover:shadow-xl transition-all duration-300"
                onClick={() => setLightboxIndex(index)}
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={img.src}
                  alt={img.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <p className="text-white font-semibold text-sm">{img.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-yellow-400 transition-colors z-10"
            onClick={() => setLightboxIndex(null)}
            data-testid="lightbox-close"
          >
            <X className="w-8 h-8" />
          </button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-yellow-400 text-4xl font-light z-10 px-3 py-1"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex - 1 + galleryImages.length) % galleryImages.length); }}
            data-testid="lightbox-prev"
          >
            &#8249;
          </button>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-yellow-400 text-4xl font-light z-10 px-3 py-1"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((lightboxIndex + 1) % galleryImages.length); }}
            data-testid="lightbox-next"
          >
            &#8250;
          </button>
          <div className="max-w-4xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={galleryImages[lightboxIndex].src}
              alt={galleryImages[lightboxIndex].title}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-3 font-medium">{galleryImages[lightboxIndex].title}</p>
          </div>
        </div>
      )}
    </div>
  );
}
